import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import charterGroupWsInitialState, {
  type CharterGroupWsState,
} from '../initialState/charterGroupWsInitialState'
import type { GroupChatMessage, ConsensusConfirmState } from '../types/charterWsTypes'
import type { GroupStage, RatifiedCharter, RenegotiationRequest } from '../types/charterTypes'

const STREAMING_MESSAGE_ID_PREFIX = 'mod_stream_'

/**
 * #573 — defense in depth for silent moderator turns.
 *
 * The server suppresses a declined turn before it is saved or broadcast, so this
 * should never match. It exists so that a sentinel which somehow reaches a
 * client (older backend, leaked token, replayed history) is never rendered and
 * never counts as "the moderator has spoken" — the derivations in
 * CharterGroupBody read this state, not the display list, which is why the
 * filter lives in the reducers rather than in the display mapper.
 */
const SILENT_SENTINEL = '[SILENT]'

const isSilentTurn = (msg: { role?: string; content?: string }) =>
  msg.role === 'moderator' && (msg.content ?? '').trim() === SILENT_SENTINEL

const charterGroupWsSlice = createSlice({
  name: 'charterGroupWs',
  initialState: charterGroupWsInitialState as CharterGroupWsState,
  reducers: {
    setGroupConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload
      if (!action.payload) {
        state.isStreaming = false
        state.isModeratorThinking = false
        state.currentStreamId = null
      }
    },

    setModeratorThinking(state, action: PayloadAction<boolean>) {
      state.isModeratorThinking = action.payload
    },

    setGroupError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },

    // chat_history on connect
    setGroupChatHistory(
      state,
      action: PayloadAction<{
        messages: GroupChatMessage[]
        current_stage: GroupStage | null
        ratification_votes: Record<string, { vote: boolean; voted_at: string }>
        consensus_records: Array<{
          section_key: string
          consensus_text: string
          agreed_at: string | null
        }>
        team_name: string | null
        amendment_mode: boolean
        charter_status: string | null
        charter_version: number
        ratified_charter: RatifiedCharter | null
        has_more?: boolean
        total_members?: number
      }>
    ) {
      const p = action.payload
      state.messages = (p.messages || []).filter((m) => !isSilentTurn(m))
      // Oldest raw id, pre-filter, so paging starts at the true boundary.
      state.pagination = {
        has_more: p.has_more ?? false,
        oldest_loaded_id: p.messages?.[0]?.id ?? null,
      }
      state.isLoadingMore = false
      state.currentStage = p.current_stage
      state.ratificationVotes = p.ratification_votes
      state.teamName = p.team_name
      state.renegotiationMode = p.amendment_mode
      state.charterStatus = p.charter_status
      state.charterVersion = p.charter_version
      state.ratifiedCharter = p.ratified_charter
      if (typeof p.total_members === 'number') {
        state.totalMembers = p.total_members
      }

      // Hydrate consensus state from DB records
      const newConsensus: ConsensusConfirmState = {}
      for (const rec of p.consensus_records || []) {
        newConsensus[rec.section_key] = {
          confirmed_by: [],
          is_confirmed: true,
          consensus_text: rec.consensus_text,
        }
      }
      state.consensusState = newConsensus
    },

    // group_message from another user (or server echo of own message)
    appendGroupMessage(state, action: PayloadAction<GroupChatMessage>) {
      const msg = action.payload

      if (isSilentTurn(msg)) return

      // Exact duplicate by ID — skip
      if (state.messages.find((m) => m.id === msg.id)) return

      // Replace the optimistic temp message (same sender + content) with the real one
      const tempIdx = state.messages.findIndex(
        (m) =>
          m.id.startsWith('temp_') &&
          m.sender_id === msg.sender_id &&
          m.content === msg.content
      )
      if (tempIdx !== -1) {
        state.messages[tempIdx] = msg
        return
      }

      state.messages.push(msg)
    },

    // Optimistic local insert when the current user sends a message
    appendGroupUserMessage(
      state,
      action: PayloadAction<{
        content: string
        sender_id: number
        sender_display_name: string
        stage: GroupStage
      }>
    ) {
      const { content, sender_id, sender_display_name, stage } = action.payload
      state.messages.push({
        id: `temp_${Date.now()}`,
        role: 'user',
        content,
        sender_display_name,
        sender_id,
        stage,
        is_intervention: false,
        is_streaming: false,
        created_at: new Date().toISOString(),
      })
    },

    // moderator_chunk
    appendOrUpdateModeratorChunk(
      state,
      action: PayloadAction<{ stream_id: string; content: string; is_first: boolean }>
    ) {
      const { stream_id, content, is_first } = action.payload
      const tempId = STREAMING_MESSAGE_ID_PREFIX + stream_id

      if (is_first && !content) {
        // Streaming start signal — create placeholder and clear thinking indicator
        state.messages.push({
          id: tempId,
          role: 'moderator',
          content: '',
          sender_display_name: 'AI Moderator',
          sender_id: null,
          stage: state.currentStage || 'opening',
          is_intervention: false,
          is_streaming: true,
          created_at: new Date().toISOString(),
        })
        state.isStreaming = true
        state.isModeratorThinking = false
        state.currentStreamId = stream_id
        return
      }

      const existing = state.messages.find((m) => m.id === tempId)
      if (existing) {
        existing.content += content
      }
    },

    // moderator_final
    finalizeModeratorMessage(
      state,
      action: PayloadAction<{
        stream_id?: string
        id: string
        content: string
        stage: GroupStage
        is_intervention: boolean
        intervention_type?: string | null
        created_at: string
      }>
    ) {
      const { stream_id, id, content, stage, is_intervention, intervention_type, created_at } =
        action.payload

      const tempId = stream_id ? STREAMING_MESSAGE_ID_PREFIX + stream_id : null

      // A declined turn should never reach here (the server drops it), but if
      // one does, discard it and clean up any placeholder it created rather
      // than rendering the raw token.
      if (isSilentTurn({ role: 'moderator', content })) {
        if (tempId) {
          state.messages = state.messages.filter((m) => m.id !== tempId)
        }
        state.isStreaming = false
        state.isModeratorThinking = false
        state.currentStreamId = null
        return
      }

      if (tempId) {
        const idx = state.messages.findIndex((m) => m.id === tempId)
        if (idx !== -1) {
          state.messages[idx] = {
            id,
            role: 'moderator',
            content,
            sender_display_name: 'AI Moderator',
            sender_id: null,
            stage,
            is_intervention,
            intervention_type: intervention_type ?? undefined,
            is_streaming: false,
            created_at,
          }
        } else if (!state.messages.some((m) => m.id === id)) {
          // No streaming placeholder: this client's socket was reconnecting
          // while the chunks were broadcast (chat history is sent before the
          // streamed message is saved, so history didn't carry it either).
          // The finalize event holds the complete text — append it rather
          // than dropping the message, or the client is stuck on "Analyzing
          // member profiles…" until a hard refresh.
          state.messages.push({
            id,
            role: 'moderator',
            content,
            sender_display_name: 'AI Moderator',
            sender_id: null,
            stage,
            is_intervention,
            intervention_type: intervention_type ?? undefined,
            is_streaming: false,
            created_at,
          })
        }
      } else {
        // No stream placeholder — append directly (e.g. turn alerts)
        const existing = state.messages.find((m) => m.id === id)
        if (!existing) {
          state.messages.push({
            id,
            role: 'moderator',
            content,
            sender_display_name: 'AI Moderator',
            sender_id: null,
            stage,
            is_intervention,
            intervention_type: intervention_type ?? undefined,
            is_streaming: false,
            created_at,
          })
        }
      }
      // A non-streamed final (stream_id null: turn alerts, nudges, the
      // ratification prompt) can land while a different, streamed turn is still
      // in flight — the ratification prompt is generated off a detached task, so
      // it races an ordinary reply. Only the owner of the live stream may clear
      // the streaming state, or the input unblocks mid-stream.
      const ownsLiveStream = !state.currentStreamId || stream_id === state.currentStreamId
      if (ownsLiveStream) {
        state.isStreaming = false
        state.currentStreamId = null
      }
      // Always safe to clear: a final means the moderator has spoken. Without
      // this a non-streamed final left the indicator spinning forever.
      state.isModeratorThinking = false
    },

    prependGroupMessages(
      state,
      action: PayloadAction<{ messages: GroupChatMessage[]; has_more: boolean }>
    ) {
      const { messages, has_more } = action.payload
      // Guard the store too: a duplicate renders as a repeated React key.
      const known = new Set(state.messages.map((m) => m.id))
      const incoming = messages.filter(
        (m) => !isSilentTurn(m) && !known.has(m.id)
      )
      state.messages = [...incoming, ...state.messages]
      state.pagination = {
        has_more,
        // Page from the oldest id received, including filtered silent turns.
        oldest_loaded_id:
          messages[0]?.id ?? state.pagination?.oldest_loaded_id ?? null,
      }
      state.isLoadingMore = false
    },

    setGroupLoadingMore(state, action: PayloadAction<boolean>) {
      state.isLoadingMore = action.payload
    },

    setCurrentStage(state, action: PayloadAction<GroupStage | null>) {
      state.currentStage = action.payload
    },

    updateTurnTracker(
      state,
      action: PayloadAction<Record<string, { message_count: number; word_count: number; consecutive_messages: number }>>
    ) {
      state.turnTracker = action.payload
    },

    setConsensusRecord(
      state,
      action: PayloadAction<{
        section_key: string
        consensus_text: string
        confirmed_by: number
      }>
    ) {
      const { section_key, consensus_text, confirmed_by } = action.payload
      if (!state.consensusState[section_key]) {
        state.consensusState[section_key] = {
          confirmed_by: [],
          is_confirmed: false,
          consensus_text: '',
        }
      }
      state.consensusState[section_key].consensus_text = consensus_text
      if (!state.consensusState[section_key].confirmed_by.includes(confirmed_by)) {
        state.consensusState[section_key].confirmed_by.push(confirmed_by)
      }
      state.consensusState[section_key].is_confirmed = true
    },

    recordRatificationVote(
      state,
      action: PayloadAction<{
        votes: Record<string, { vote: boolean; voted_at: string }>
        total_members: number
        yes_count: number
      }>
    ) {
      state.ratificationVotes = action.payload.votes
      state.totalMembers = action.payload.total_members
      state.ratificationVoteCount = action.payload.yes_count
    },

    setCharterRatified(
      state,
      action: PayloadAction<{ charter: RatifiedCharter; version: number }>
    ) {
      state.ratifiedCharter = action.payload.charter
      state.charterVersion = action.payload.version
      state.charterStatus = 'locked'
      state.ratificationFinalizing = false
    },

    setRenegotiationMode(state, action: PayloadAction<boolean>) {
      state.renegotiationMode = action.payload
    },

    setPendingRenegotiationRequest(
      state,
      action: PayloadAction<RenegotiationRequest | null>
    ) {
      state.pendingRenegotiationRequest = action.payload
    },

    // Live vote tally update for the active amendment request (keyed by RoomMember id).
    applyRenegotiationVotes(
      state,
      action: PayloadAction<Record<string, { vote: boolean; voted_at: string }>>
    ) {
      if (state.pendingRenegotiationRequest) {
        state.pendingRenegotiationRequest.votes = action.payload
      }
    },

    setRatificationPromptActive(state, action: PayloadAction<boolean>) {
      state.ratificationPromptActive = action.payload
    },

    setRatificationFinalizing(state, action: PayloadAction<boolean>) {
      state.ratificationFinalizing = action.payload
    },

    setGroupCharterStatus(
      state,
      action: PayloadAction<{ status: string; version?: number }>
    ) {
      state.charterStatus = action.payload.status
      if (action.payload.version !== undefined) {
        state.charterVersion = action.payload.version
      }
    },

  },
})

export const {
  setGroupConnected,
  setGroupError,
  setGroupChatHistory,
  appendGroupMessage,
  appendGroupUserMessage,
  appendOrUpdateModeratorChunk,
  finalizeModeratorMessage,
  prependGroupMessages,
  setGroupLoadingMore,
  setCurrentStage,
  updateTurnTracker,
  setConsensusRecord,
  recordRatificationVote,
  setCharterRatified,
  setRenegotiationMode,
  setPendingRenegotiationRequest,
  applyRenegotiationVotes,
  setRatificationPromptActive,
  setRatificationFinalizing,
  setGroupCharterStatus,
  setModeratorThinking,
} = charterGroupWsSlice.actions

export default charterGroupWsSlice.reducer
